import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Phone, ArrowLeft, X } from 'lucide-react';
import OTPVerification from './OTPVerification';

interface OTPLoginProps {
  onClose: () => void;
  onBack: () => void;
  redirectAfterLogin?: string;
}

const OTPLogin: React.FC<OTPLoginProps> = ({ onClose, onBack, redirectAfterLogin }) => {
  const [phone, setPhone] = useState('');
  const [showOTPVerification, setShowOTPVerification] = useState(false);
  const { requestOTP, loading, error, clearError } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!phone.trim()) {
      return;
    }

    try {
      await requestOTP(phone);
      setShowOTPVerification(true);
    } catch (err) {
      // Error is handled by auth context
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhone(e.target.value);
    if (error) {
      clearError();
    }
  };

  if (showOTPVerification) {
    return (
      <OTPVerification
        phone={phone}
        onClose={onClose}
        onBack={() => setShowOTPVerification(false)}
        onSuccess={onClose}
        redirectAfterLogin={redirectAfterLogin}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-4 sm:p-6 md:p-8 relative max-h-[90vh] overflow-y-auto">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="h-6 w-6" />
        </button>

        {/* Back Button */}
        <button
          onClick={onBack}
          className="absolute top-4 left-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <ArrowLeft className="h-6 w-6" />
        </button>

        <div className="text-center mb-6 sm:mb-8 mt-2 sm:mt-4">
          <div className="flex justify-center mb-4">
            <img 
              src="/logo.png" 
              alt="Durga Puja Network" 
              className="h-20 w-20 sm:h-24 sm:w-24 object-contain"
              onError={(e) => e.currentTarget.style.display = 'none'}
            />
          </div>
          <div className="w-16 h-16 bg-dpn-pink bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Phone className="h-8 w-8 text-dpn-pink" />
          </div>
          <h2 className="font-runtime text-2xl sm:text-3xl font-bold text-black mb-2">
            Login with OTP
          </h2>
          <p className="font-lucy-rose text-gray-600">
            Enter your phone number to receive a verification code
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2 font-lucy-rose">
              Phone Number
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="tel"
                id="phone"
                required
                value={phone}
                onChange={handlePhoneChange}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dpn-pink focus:border-transparent outline-none font-lucy-rose"
                placeholder="+1 (555) 123-4567"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500 font-lucy-rose">
              We'll send you a 6-digit verification code
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-600 text-sm font-lucy-rose">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !phone.trim()}
            className="w-full bg-dpn-pink hover:bg-dpn-raspberry text-white font-lucy-rose font-semibold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
            ) : null}
            {loading ? 'Sending OTP...' : 'Send Verification Code'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="font-lucy-rose text-xs text-gray-500">
            By continuing, you agree to receive WhatsApp messages for verification purposes.
          </p>
        </div>
      </div>
    </div>
  );
};

export default OTPLogin;